import { useMutation, useQuery, type UseQueryOptions } from "@tanstack/react-query";
import {
  AddViaJoinRequestSchema,
  AddViaJoinResponseSchema,
  CustomerCardsResponseSchema,
  CustomerMeResponseSchema,
  IdentityGenerateResponseSchema,
  LoginRequestSchema,
  LoginResponseSchema,
  PasswordResetCompleteRequestSchema,
  PasswordResetCompleteResponseSchema,
  PasswordResetRequestResponseSchema,
  PasswordResetRequestSchema,
  PasswordResetVerifyOtpRequestSchema,
  PasswordResetVerifyOtpResponseSchema,
  PublicTenantSchema,
  RefreshAuthRequestSchema,
  RefreshAuthResponseSchema,
  SetCustomerPasswordRequestSchema,
  SetCustomerPasswordResponseSchema,
  SignupRequestSchema,
  SignupResponseSchema,
  SignupViaJoinRequestSchema,
  SignupViaJoinResponseSchema,
  TenantCardSchema,
  TenantMeResponseSchema,
  TenantSettingsUpdateRequestSchema,
  VerifyEmailConfirmRequestSchema,
  VerifyEmailConfirmResponseSchema,
  VerifyEmailRequestResponseSchema,
  VerifyPhoneConfirmRequestSchema,
  VerifyPhoneConfirmResponseSchema,
  VerifyPhoneRequestResponseSchema,
  type AddViaJoinRequest,
  type AddViaJoinResponse,
  type ApiError,
  type CustomerCardsResponse,
  type CustomerMeResponse,
  type IdentityGenerateResponse,
  type LoginRequest,
  type LoginResponse,
  type PublicTenant,
  type RefreshAuthRequest,
  type RefreshAuthResponse,
  type SignupRequest,
  type SignupResponse,
  type SignupViaJoinRequest,
  type SignupViaJoinResponse,
  type TenantCard,
  type TenantMeResponse,
  type TenantSettingsUpdateRequest,
  type VerifyEmailConfirmRequest,
  type VerifyEmailConfirmResponse,
  type VerifyEmailRequestResponse,
  type VerifyPhoneConfirmRequest,
  type VerifyPhoneConfirmResponse,
  type VerifyPhoneRequestResponse,
} from "@orbit/api-zod";
import { z } from "zod";

export class OrbitApiError extends Error {
  status: number;
  apiError: ApiError;
  constructor(status: number, apiError: ApiError) {
    super(apiError.message);
    this.name = "OrbitApiError";
    this.status = status;
    this.apiError = apiError;
  }
}

// Re-export the wire-level types so consumers can pull them from a
// single entry point alongside the hooks. Without explicit
// `export type` lines a `type X` import above is private to this
// module under TS verbatim/isolated-modules emit.
export type {
  AddViaJoinRequest,
  AddViaJoinResponse,
  ApiError,
  CustomerCardsResponse,
  CustomerInboxListResponse,
  CustomerInboxMarkAllReadResponse,
  CustomerInboxMessageResponse,
  CustomerInboxUnreadCountResponse,
  CustomerMeResponse,
  IdentityGenerateResponse,
  LoginRequest,
  LoginResponse,
  PasswordResetCompleteRequest,
  PasswordResetCompleteResponse,
  PasswordResetRequest,
  PasswordResetRequestResponse,
  PasswordResetVerifyOtpRequest,
  PasswordResetVerifyOtpResponse,
  PublicTenant,
  RefreshAuthRequest,
  RefreshAuthResponse,
  SetCustomerPasswordRequest,
  SetCustomerPasswordResponse,
  SignupRequest,
  SignupResponse,
  SignupViaJoinRequest,
  SignupViaJoinResponse,
  TenantCard,
  TenantMeResponse,
  TenantSettingsUpdateRequest,
  VerifyEmailConfirmRequest,
  VerifyEmailConfirmResponse,
  VerifyEmailRequestResponse,
  VerifyPhoneConfirmRequest,
  VerifyPhoneConfirmResponse,
  VerifyPhoneRequestResponse,
} from "@orbit/api-zod";

export type AuthTokenProvider = () => string | null;

export interface OrbitClientConfig {
  baseUrl?: string;
  getAccessToken?: AuthTokenProvider;
  /**
   * Optional silent-refresh hook. When the server returns 401 on an
   * authenticated request and `tryRefreshAccessToken` is configured, the
   * request layer calls it ONCE per failed request. If it resolves to a
   * truthy access token (or `true`), the original request is retried with
   * the freshest token from `getAccessToken`. If it resolves falsy, the
   * request fails and `onUnauthorized` fires.
   */
  tryRefreshAccessToken?: () => Promise<string | boolean | null>;
  onUnauthorized?: () => void | Promise<void>;
}

let config: OrbitClientConfig = { baseUrl: "" };

export function configureOrbitClient(next: OrbitClientConfig) {
  config = { ...config, ...next };
}

const ResponseEnvelope = z.object({
  data: z.unknown().optional(),
  error: z
    .object({ code: z.string(), message: z.string(), details: z.unknown().optional() })
    .optional(),
  requestId: z.string(),
});

async function request<TIn, TOut extends z.ZodTypeAny>(opts: {
  method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  path: string;
  body?: TIn;
  responseSchema: TOut;
  authRequired?: boolean;
  /** internal — true on the second attempt after a silent refresh */
  _retried?: boolean;
}): Promise<z.infer<TOut>> {
  const { method, path, body, responseSchema, authRequired, _retried } = opts;
  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";

  if (authRequired) {
    const token = config.getAccessToken?.();
    if (!token) throw new OrbitApiError(401, { code: "NO_TOKEN", message: "Not signed in." });
    headers.Authorization = `Bearer ${token}`;
  }

  const url = `${config.baseUrl ?? ""}${path}`;
  const res = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  let parsedBody: unknown = null;
  try {
    parsedBody = await res.json();
  } catch {
    parsedBody = null;
  }

  const envelope = ResponseEnvelope.safeParse(parsedBody);
  if (!envelope.success) {
    throw new OrbitApiError(res.status, {
      code: "BAD_RESPONSE",
      message: `Server returned malformed response (${res.status})`,
    });
  }

  if (!res.ok || envelope.data.error) {
    // Silent refresh: on the FIRST 401 for an authenticated request, give
    // the configured refresh hook one chance to mint a new access token.
    // Successful refresh retries the original request once with the new
    // token. Failed refresh (or no hook configured) falls through to the
    // existing onUnauthorized + throw path.
    if (
      res.status === 401 &&
      authRequired &&
      !_retried &&
      typeof config.tryRefreshAccessToken === "function"
    ) {
      let refreshed: string | boolean | null = null;
      try {
        refreshed = await config.tryRefreshAccessToken();
      } catch {
        refreshed = null;
      }
      if (refreshed) {
        return request({ ...opts, _retried: true });
      }
    }
    if (res.status === 401) await config.onUnauthorized?.();
    throw new OrbitApiError(
      res.status,
      envelope.data.error ?? { code: "HTTP_ERROR", message: `HTTP ${res.status}` },
    );
  }

  return responseSchema.parse(envelope.data.data);
}

// ----- Hooks -----

export function useGetTenantByShortCode(
  shortCode: string | undefined,
  options?: Omit<UseQueryOptions<PublicTenant, OrbitApiError>, "queryKey" | "queryFn">,
) {
  return useQuery<PublicTenant, OrbitApiError>({
    queryKey: ["public-tenant", shortCode],
    queryFn: () =>
      request({
        method: "GET",
        path: `/api/v1/public/tenant-by-short-code/${encodeURIComponent(shortCode ?? "")}`,
        responseSchema: PublicTenantSchema,
      }),
    enabled: Boolean(shortCode),
    retry: false,
    ...options,
  });
}

export function useSignupViaJoin() {
  return useMutation<SignupViaJoinResponse, OrbitApiError, SignupViaJoinRequest>({
    mutationFn: (body) =>
      request({
        method: "POST",
        path: "/api/v1/enrollment/signup-via-join",
        body: SignupViaJoinRequestSchema.parse(body),
        responseSchema: SignupViaJoinResponseSchema,
      }),
  });
}

export function useAddViaJoin() {
  return useMutation<AddViaJoinResponse, OrbitApiError, AddViaJoinRequest>({
    mutationFn: (body) =>
      request({
        method: "POST",
        path: "/api/v1/enrollment/add-via-join",
        body: AddViaJoinRequestSchema.parse(body),
        responseSchema: AddViaJoinResponseSchema,
        authRequired: true,
      }),
  });
}

// ----- Password auth (replaces the old OTP-as-signin hooks) -----
//
// `useSignup` is one-shot: name + phone + password (+ optional email)
// → session tokens. The customer is created with phoneVerified=false
// and emailVerified=false; verification badges are unlocked later via
// the verify-{phone,email} hooks below from the profile page.
export function useSignup() {
  return useMutation<SignupResponse, OrbitApiError, SignupRequest>({
    mutationFn: (body) =>
      request({
        method: "POST",
        path: "/api/v1/auth/signup",
        body: SignupRequestSchema.parse(body),
        responseSchema: SignupResponseSchema,
      }),
  });
}

export function useLogin() {
  return useMutation<LoginResponse, OrbitApiError, LoginRequest>({
    mutationFn: (body) =>
      request({
        method: "POST",
        path: "/api/v1/auth/login",
        body: LoginRequestSchema.parse(body),
        responseSchema: LoginResponseSchema,
      }),
  });
}

// ----- Verify phone (one-time, post-login) -----
//
// `useVerifyPhoneRequest` mints a 6-digit OTP and dispatches it to the
// customer's stored phone. `useVerifyPhoneConfirm` accepts the same
// otpRequestId and the typed code; on success the server flips the
// `phoneVerified` flag and the next /customer/me reflects the badge.
// 503 EMAIL/SMS_NOT_CONFIGURED bubbles through as OrbitApiError(503).
export function useVerifyPhoneRequest() {
  return useMutation<VerifyPhoneRequestResponse, OrbitApiError, void>({
    mutationFn: () =>
      request({
        method: "POST",
        path: "/api/v1/auth/verify-phone/request",
        body: {},
        responseSchema: VerifyPhoneRequestResponseSchema,
        authRequired: true,
      }),
  });
}

export function useVerifyPhoneConfirm() {
  return useMutation<
    VerifyPhoneConfirmResponse,
    OrbitApiError,
    VerifyPhoneConfirmRequest
  >({
    mutationFn: (body) =>
      request({
        method: "POST",
        path: "/api/v1/auth/verify-phone/confirm",
        body: VerifyPhoneConfirmRequestSchema.parse(body),
        responseSchema: VerifyPhoneConfirmResponseSchema,
        authRequired: true,
      }),
  });
}

// ----- Verify email (one-time, post-login) -----
//
// Same shape as verify-phone. The /request endpoint requires that an
// email is already on file (set at signup or via PATCH /customer/me);
// the server returns 400 EMAIL_NOT_ON_PROFILE if not.
export function useVerifyEmailRequest() {
  return useMutation<VerifyEmailRequestResponse, OrbitApiError, void>({
    mutationFn: () =>
      request({
        method: "POST",
        path: "/api/v1/auth/verify-email/request",
        body: {},
        responseSchema: VerifyEmailRequestResponseSchema,
        authRequired: true,
      }),
  });
}

export function useVerifyEmailConfirm() {
  return useMutation<
    VerifyEmailConfirmResponse,
    OrbitApiError,
    VerifyEmailConfirmRequest
  >({
    mutationFn: (body) =>
      request({
        method: "POST",
        path: "/api/v1/auth/verify-email/confirm",
        body: VerifyEmailConfirmRequestSchema.parse(body),
        responseSchema: VerifyEmailConfirmResponseSchema,
        authRequired: true,
      }),
  });
}

export function useRefreshAuth() {
  return useMutation<RefreshAuthResponse, OrbitApiError, RefreshAuthRequest>({
    mutationFn: (body) =>
      request({
        method: "POST",
        path: "/api/v1/auth/refresh",
        body: RefreshAuthRequestSchema.parse(body),
        responseSchema: RefreshAuthResponseSchema,
      }),
  });
}

export function useGetCustomerMe(
  options?: Omit<UseQueryOptions<CustomerMeResponse, OrbitApiError>, "queryKey" | "queryFn">,
) {
  return useQuery<CustomerMeResponse, OrbitApiError>({
    queryKey: ["customer-me"],
    queryFn: () =>
      request({
        method: "GET",
        path: "/api/v1/customer/me",
        responseSchema: CustomerMeResponseSchema,
        authRequired: true,
      }),
    retry: false,
    ...options,
  });
}

export function useGetTenantMe(
  options?: Omit<UseQueryOptions<TenantMeResponse, OrbitApiError>, "queryKey" | "queryFn">,
) {
  return useQuery<TenantMeResponse, OrbitApiError>({
    queryKey: ["tenant-me"],
    queryFn: () =>
      request({
        method: "GET",
        path: "/api/v1/tenant/me",
        responseSchema: TenantMeResponseSchema,
        authRequired: true,
      }),
    retry: false,
    ...options,
  });
}

/**
 * PATCH /api/v1/tenant/settings — merchant updates the per-transaction
 * scan ceiling (and any future per-tenant scan settings). The server
 * echoes back the freshest TenantMeResponse so the dashboard can
 * re-render straight off the mutation result without a follow-up GET;
 * callers that hold a `useGetTenantMe` query in cache should still
 * invalidate `["tenant-me"]` from their `onSuccess` so other consumers
 * see the new value.
 */
export function useUpdateTenantSettings() {
  return useMutation<TenantMeResponse, OrbitApiError, TenantSettingsUpdateRequest>({
    mutationFn: (body) =>
      request({
        method: "PATCH",
        path: "/api/v1/tenant/settings",
        body: TenantSettingsUpdateRequestSchema.parse(body),
        responseSchema: TenantMeResponseSchema,
        authRequired: true,
      }),
  });
}

// ===== Phase 2 hooks =====

/**
 * Mints a fresh 60-second Identity QR token. Use as a mutation (not a
 * query) so the caller controls when to refresh — the QR modal calls
 * `mutateAsync()` on a 55s timer, and falls back to a manual refresh on
 * window-focus or token-expiry events.
 */
export function useGenerateIdentityQr() {
  return useMutation<IdentityGenerateResponse, OrbitApiError, void>({
    mutationFn: () =>
      request({
        method: "GET",
        path: "/api/v1/identity/generate",
        responseSchema: IdentityGenerateResponseSchema,
        authRequired: true,
      }),
  });
}

export function useGetMyCards(
  options?: Omit<UseQueryOptions<CustomerCardsResponse, OrbitApiError>, "queryKey" | "queryFn">,
) {
  return useQuery<CustomerCardsResponse, OrbitApiError>({
    queryKey: ["my-cards"],
    queryFn: () =>
      request({
        method: "GET",
        path: "/api/v1/customer/me/cards",
        responseSchema: CustomerCardsResponseSchema,
        authRequired: true,
      }),
    retry: false,
    ...options,
  });
}

// =====================================================================
// Phase 12 — Customer profile + connected merchants management
// =====================================================================
//
// Hook surface for the Phase 12 customer-side legal pages. Conventions:
//   • Reads use `useQuery` keyed off a stable tuple so two consumers
//     share the same cache.
//   • Writes use `useMutation` and the consumer is responsible for
//     calling `queryClient.invalidateQueries` on success — the hooks
//     themselves stay framework-agnostic so they don't import a
//     QueryClient instance (which would break SSR).
//   • Cursor-paginated readers (`useConsentHistory`) take an explicit
//     `cursor` argument and re-key on it; consumers compose the page
//     loop manually rather than via TanStack's `useInfiniteQuery` to
//     keep the hook surface minimal.
//   • The data export hook is a raw fetch (NOT through the envelope
//     parser) because the response is a downloadable JSON blob whose
//     shape lives in CustomerDataExportSchema for callers that want
//     to re-import it client-side.

import {
  AccountDeleteCancelResponseSchema,
  AccountDeleteRequestRequestSchema,
  AccountDeleteRequestResponseSchema,
  AccountDeleteStatusResponseSchema,
  ConnectedMerchantsResponseSchema,
  ConsentHistoryResponseSchema,
  CustomerProfileResponseSchema,
  CustomerProfileUpdateRequestSchema,
  EmailChangeCancelRequestSchema,
  EmailChangeCancelResponseSchema,
  EmailChangeStartRequestSchema,
  EmailChangeStartResponseSchema,
  EmailChangeVerifyRequestSchema,
  EmailChangeVerifyResponseSchema,
  M42MarketingToggleRequestSchema,
  M42MarketingToggleResponseSchema,
  PhoneChangeCancelRequestSchema,
  PhoneChangeCancelResponseSchema,
  PhoneChangeStartRequestSchema,
  PhoneChangeStartResponseSchema,
  PhoneChangeVerifyNewRequestSchema,
  PhoneChangeVerifyNewResponseSchema,
  PhoneChangeVerifyOldRequestSchema,
  PhoneChangeVerifyOldResponseSchema,
  RevokeMerchantRequestSchema,
  RevokeMerchantResponseSchema,
  TenantMarketingToggleRequestSchema,
  TenantMarketingToggleResponseSchema,
  type AccountDeleteCancelResponse,
  type AccountDeleteRequestRequest,
  type AccountDeleteRequestResponse,
  type AccountDeleteStatusResponse,
  type ConnectedMerchantsResponse,
  type ConsentHistoryResponse,
  type CustomerProfileResponse,
  type CustomerProfileUpdateRequest,
  type EmailChangeCancelRequest,
  type EmailChangeCancelResponse,
  type EmailChangeStartRequest,
  type EmailChangeStartResponse,
  type EmailChangeVerifyRequest,
  type EmailChangeVerifyResponse,
  type M42MarketingToggleRequest,
  type M42MarketingToggleResponse,
  type PhoneChangeCancelRequest,
  type PhoneChangeCancelResponse,
  type PhoneChangeStartRequest,
  type PhoneChangeStartResponse,
  type PhoneChangeVerifyNewRequest,
  type PhoneChangeVerifyNewResponse,
  type PhoneChangeVerifyOldRequest,
  type PhoneChangeVerifyOldResponse,
  type RevokeMerchantRequest,
  type RevokeMerchantResponse,
  type TenantMarketingToggleRequest,
  type TenantMarketingToggleResponse,
  type CustomerDataExport,
} from "@orbit/api-zod";

// ----- Profile ------------------------------------------------------

export function useCustomerProfile(
  options?: Omit<
    UseQueryOptions<CustomerProfileResponse, OrbitApiError>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery<CustomerProfileResponse, OrbitApiError>({
    queryKey: ["customer-profile"],
    queryFn: () =>
      request({
        method: "GET",
        path: "/api/v1/customer/me/profile",
        responseSchema: CustomerProfileResponseSchema,
        authRequired: true,
      }),
    retry: false,
    ...options,
  });
}

export function useUpdateCustomerProfile() {
  return useMutation<
    CustomerProfileResponse,
    OrbitApiError,
    CustomerProfileUpdateRequest
  >({
    mutationFn: (body) =>
      request({
        method: "PATCH",
        path: "/api/v1/customer/me/profile",
        body: CustomerProfileUpdateRequestSchema.parse(body),
        responseSchema: CustomerProfileResponseSchema,
        authRequired: true,
      }),
  });
}

// ----- Phone change (two-OTP) --------------------------------------

export function useStartPhoneChange() {
  return useMutation<
    PhoneChangeStartResponse,
    OrbitApiError,
    PhoneChangeStartRequest
  >({
    mutationFn: (body) =>
      request({
        method: "POST",
        path: "/api/v1/customer/me/phone-change/start",
        body: PhoneChangeStartRequestSchema.parse(body),
        responseSchema: PhoneChangeStartResponseSchema,
        authRequired: true,
      }),
  });
}

export function useVerifyPhoneChangeOld() {
  return useMutation<
    PhoneChangeVerifyOldResponse,
    OrbitApiError,
    PhoneChangeVerifyOldRequest
  >({
    mutationFn: (body) =>
      request({
        method: "POST",
        path: "/api/v1/customer/me/phone-change/verify-old",
        body: PhoneChangeVerifyOldRequestSchema.parse(body),
        responseSchema: PhoneChangeVerifyOldResponseSchema,
        authRequired: true,
      }),
  });
}

export function useVerifyPhoneChangeNew() {
  return useMutation<
    PhoneChangeVerifyNewResponse,
    OrbitApiError,
    PhoneChangeVerifyNewRequest
  >({
    mutationFn: (body) =>
      request({
        method: "POST",
        path: "/api/v1/customer/me/phone-change/verify-new",
        body: PhoneChangeVerifyNewRequestSchema.parse(body),
        responseSchema: PhoneChangeVerifyNewResponseSchema,
        authRequired: true,
      }),
  });
}

export function useCancelPhoneChange() {
  return useMutation<
    PhoneChangeCancelResponse,
    OrbitApiError,
    PhoneChangeCancelRequest
  >({
    mutationFn: (body) =>
      request({
        method: "POST",
        path: "/api/v1/customer/me/phone-change/cancel",
        body: PhoneChangeCancelRequestSchema.parse(body),
        responseSchema: PhoneChangeCancelResponseSchema,
        authRequired: true,
      }),
  });
}

// ----- Email change -------------------------------------------------

export function useStartEmailChange() {
  return useMutation<
    EmailChangeStartResponse,
    OrbitApiError,
    EmailChangeStartRequest
  >({
    mutationFn: (body) =>
      request({
        method: "POST",
        path: "/api/v1/customer/me/email-change/start",
        body: EmailChangeStartRequestSchema.parse(body),
        responseSchema: EmailChangeStartResponseSchema,
        authRequired: true,
      }),
  });
}

export function useVerifyEmailChange() {
  return useMutation<
    EmailChangeVerifyResponse,
    OrbitApiError,
    EmailChangeVerifyRequest
  >({
    mutationFn: (body) =>
      request({
        method: "POST",
        path: "/api/v1/customer/me/email-change/verify",
        body: EmailChangeVerifyRequestSchema.parse(body),
        responseSchema: EmailChangeVerifyResponseSchema,
        authRequired: true,
      }),
  });
}

export function useCancelEmailChange() {
  return useMutation<
    EmailChangeCancelResponse,
    OrbitApiError,
    EmailChangeCancelRequest
  >({
    mutationFn: (body) =>
      request({
        method: "POST",
        path: "/api/v1/customer/me/email-change/cancel",
        body: EmailChangeCancelRequestSchema.parse(body),
        responseSchema: EmailChangeCancelResponseSchema,
        authRequired: true,
      }),
  });
}

// ----- Connected merchants -----------------------------------------

export function useConnectedMerchants(
  options?: Omit<
    UseQueryOptions<ConnectedMerchantsResponse, OrbitApiError>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery<ConnectedMerchantsResponse, OrbitApiError>({
    queryKey: ["connected-merchants"],
    queryFn: () =>
      request({
        method: "GET",
        path: "/api/v1/customer/me/merchants",
        responseSchema: ConnectedMerchantsResponseSchema,
        authRequired: true,
      }),
    retry: false,
    ...options,
  });
}

export function useRevokeMerchant() {
  return useMutation<
    RevokeMerchantResponse,
    OrbitApiError,
    { tenantCustomerId: string; body: RevokeMerchantRequest }
  >({
    mutationFn: ({ tenantCustomerId, body }) =>
      request({
        method: "POST",
        path: `/api/v1/customer/me/merchants/${encodeURIComponent(tenantCustomerId)}/revoke`,
        body: RevokeMerchantRequestSchema.parse(body),
        responseSchema: RevokeMerchantResponseSchema,
        authRequired: true,
      }),
  });
}

export function useTenantMarketingToggle() {
  return useMutation<
    TenantMarketingToggleResponse,
    OrbitApiError,
    { tenantCustomerId: string; body: TenantMarketingToggleRequest }
  >({
    mutationFn: ({ tenantCustomerId, body }) =>
      request({
        method: "PUT",
        path: `/api/v1/customer/me/merchants/${encodeURIComponent(tenantCustomerId)}/marketing`,
        body: TenantMarketingToggleRequestSchema.parse(body),
        responseSchema: TenantMarketingToggleResponseSchema,
        authRequired: true,
      }),
  });
}

export function useM42MarketingToggle() {
  return useMutation<
    M42MarketingToggleResponse,
    OrbitApiError,
    M42MarketingToggleRequest
  >({
    mutationFn: (body) =>
      request({
        method: "PUT",
        path: "/api/v1/customer/me/marketing/m42",
        body: M42MarketingToggleRequestSchema.parse(body),
        responseSchema: M42MarketingToggleResponseSchema,
        authRequired: true,
      }),
  });
}

// ----- Consent history ---------------------------------------------

export function useConsentHistory(
  args: { cursor?: string; limit?: number } = {},
  options?: Omit<
    UseQueryOptions<ConsentHistoryResponse, OrbitApiError>,
    "queryKey" | "queryFn"
  >,
) {
  const limit = args.limit ?? 20;
  const cursor = args.cursor;
  return useQuery<ConsentHistoryResponse, OrbitApiError>({
    queryKey: ["consent-history", { cursor: cursor ?? null, limit }],
    queryFn: () => {
      const qs = new URLSearchParams();
      qs.set("limit", String(limit));
      if (cursor) qs.set("cursor", cursor);
      return request({
        method: "GET",
        path: `/api/v1/customer/me/consents?${qs.toString()}`,
        responseSchema: ConsentHistoryResponseSchema,
        authRequired: true,
      });
    },
    retry: false,
    ...options,
  });
}

// ----- Data export -------------------------------------------------

/**
 * Triggers a synchronous JSON download of the customer's full data
 * export. Returns the parsed body so the caller can both trigger a
 * browser download AND pass the parsed payload into the UI (e.g. to
 * show a download summary). The endpoint sets Content-Disposition so
 * the browser would auto-save it; we still parse the JSON ourselves
 * so we can drive a "data exported just now" toast.
 */
export async function downloadCustomerDataExport(
  baseUrl: string | undefined,
  accessToken: string,
): Promise<CustomerDataExport> {
  const res = await fetch(`${baseUrl ?? ""}/api/v1/customer/me/data-export`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  });
  if (!res.ok) {
    let errBody: { error?: { code?: string; message?: string } } | null = null;
    try {
      errBody = (await res.json()) as { error?: { code?: string; message?: string } };
    } catch {
      errBody = null;
    }
    throw new OrbitApiError(res.status, {
      code: errBody?.error?.code ?? "EXPORT_FAILED",
      message: errBody?.error?.message ?? `HTTP ${res.status}`,
    });
  }
  const body = (await res.json()) as CustomerDataExport;
  return body;
}

export function useExportCustomerData() {
  return useMutation<CustomerDataExport, OrbitApiError, void>({
    mutationFn: () => {
      const token = config.getAccessToken?.();
      if (!token) {
        throw new OrbitApiError(401, {
          code: "NO_TOKEN",
          message: "Not signed in.",
        });
      }
      return downloadCustomerDataExport(config.baseUrl, token);
    },
  });
}

// ----- Account deletion --------------------------------------------

export function useAccountDeletionStatus(
  options?: Omit<
    UseQueryOptions<AccountDeleteStatusResponse, OrbitApiError>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery<AccountDeleteStatusResponse, OrbitApiError>({
    queryKey: ["account-deletion-status"],
    queryFn: () =>
      request({
        method: "GET",
        path: "/api/v1/customer/me/account-delete/status",
        responseSchema: AccountDeleteStatusResponseSchema,
        authRequired: true,
      }),
    retry: false,
    ...options,
  });
}

export function useRequestAccountDeletion() {
  return useMutation<
    AccountDeleteRequestResponse,
    OrbitApiError,
    AccountDeleteRequestRequest
  >({
    mutationFn: (body) =>
      request({
        method: "POST",
        path: "/api/v1/customer/me/account-delete/request",
        body: AccountDeleteRequestRequestSchema.parse(body),
        responseSchema: AccountDeleteRequestResponseSchema,
        authRequired: true,
      }),
  });
}

export function useCancelAccountDeletion() {
  return useMutation<AccountDeleteCancelResponse, OrbitApiError, void>({
    mutationFn: () =>
      request({
        method: "POST",
        path: "/api/v1/customer/me/account-delete/cancel",
        responseSchema: AccountDeleteCancelResponseSchema,
        authRequired: true,
      }),
  });
}

// =====================================================================
// Phase 15 — In-app Inbox
// =====================================================================
//
// Notes for callers:
//   • The list and unread-count queries share the "inbox" query-key
//     prefix, so a successful markRead/markAllRead can invalidate both
//     in one shot via queryClient.invalidateQueries({ queryKey: ["inbox"] }).
//   • markRead is best-effort idempotent on the server. The hook does
//     NOT optimistically mutate the list — let the invalidation refetch
//     so the unread count and list stay in lock-step.
//   • The Inbox detail screen passes the row id directly; the hook
//     therefore takes id as a parameter rather than hard-coding it.

import { useQueryClient } from "@tanstack/react-query";

import {
  CustomerInboxListResponseSchema,
  CustomerInboxMarkAllReadResponseSchema,
  CustomerInboxMessageResponseSchema,
  CustomerInboxUnreadCountResponseSchema,
  type CustomerInboxListResponse,
  type CustomerInboxMarkAllReadResponse,
  type CustomerInboxMessageResponse,
  type CustomerInboxUnreadCountResponse,
} from "@orbit/api-zod";

const INBOX_KEY = ["inbox"] as const;

function invalidateInbox(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: INBOX_KEY });
}

export function useCustomerInboxList(
  params: { cursor?: string; limit?: number; unreadOnly?: boolean } = {},
  options?: Omit<
    UseQueryOptions<CustomerInboxListResponse, OrbitApiError>,
    "queryKey" | "queryFn"
  >,
) {
  const qs = new URLSearchParams();
  if (params.cursor) qs.set("cursor", params.cursor);
  if (params.limit != null) qs.set("limit", String(params.limit));
  if (params.unreadOnly) qs.set("unreadOnly", "true");
  const query = qs.toString();
  return useQuery<CustomerInboxListResponse, OrbitApiError>({
    queryKey: [...INBOX_KEY, "list", params],
    queryFn: () =>
      request({
        method: "GET",
        path: `/api/v1/customer/me/inbox${query ? `?${query}` : ""}`,
        responseSchema: CustomerInboxListResponseSchema,
        authRequired: true,
      }),
    retry: false,
    ...options,
  });
}

export function useCustomerInboxUnreadCount(
  options?: Omit<
    UseQueryOptions<CustomerInboxUnreadCountResponse, OrbitApiError>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery<CustomerInboxUnreadCountResponse, OrbitApiError>({
    queryKey: [...INBOX_KEY, "unread-count"],
    queryFn: () =>
      request({
        method: "GET",
        path: "/api/v1/customer/me/inbox/unread-count",
        responseSchema: CustomerInboxUnreadCountResponseSchema,
        authRequired: true,
      }),
    retry: false,
    ...options,
  });
}

export function useCustomerInboxMessage(
  id: string | undefined,
  options?: Omit<
    UseQueryOptions<CustomerInboxMessageResponse, OrbitApiError>,
    "queryKey" | "queryFn" | "enabled"
  >,
) {
  return useQuery<CustomerInboxMessageResponse, OrbitApiError>({
    queryKey: [...INBOX_KEY, "message", id],
    queryFn: () =>
      request({
        method: "GET",
        path: `/api/v1/customer/me/inbox/${encodeURIComponent(id ?? "")}`,
        responseSchema: CustomerInboxMessageResponseSchema,
        authRequired: true,
      }),
    enabled: Boolean(id),
    retry: false,
    ...options,
  });
}

export function useMarkInboxMessageRead() {
  const qc = useQueryClient();
  return useMutation<{ readAt: string }, OrbitApiError, { id: string }>({
    mutationFn: ({ id }) =>
      request({
        method: "POST",
        path: `/api/v1/customer/me/inbox/${encodeURIComponent(id)}/read`,
        responseSchema: z.object({ readAt: z.string().datetime() }),
        authRequired: true,
      }),
    onSuccess: () => invalidateInbox(qc),
  });
}

export function useMarkInboxMessageUnread() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, OrbitApiError, { id: string }>({
    mutationFn: ({ id }) =>
      request({
        method: "POST",
        path: `/api/v1/customer/me/inbox/${encodeURIComponent(id)}/unread`,
        responseSchema: z.object({ ok: z.literal(true) }),
        authRequired: true,
      }),
    onSuccess: () => invalidateInbox(qc),
  });
}

export function useMarkInboxAllRead() {
  const qc = useQueryClient();
  return useMutation<CustomerInboxMarkAllReadResponse, OrbitApiError, void>({
    mutationFn: () =>
      request({
        method: "POST",
        path: "/api/v1/customer/me/inbox/mark-all-read",
        responseSchema: CustomerInboxMarkAllReadResponseSchema,
        authRequired: true,
      }),
    onSuccess: () => invalidateInbox(qc),
  });
}

// Note: tenant-side inbox endpoints (POST /tenant/me/inbox/send, GET
// /tenant/me/inbox/sent) are NOT exposed as hooks here. The shared
// `configureOrbitClient` token provider returns the *customer* access
// token, but tenant routes are authenticated by the merchant
// `orbit_live_<prefix>_<secret>` API token persisted under a
// disjoint storage key (lib/merchantAuth.ts). The MerchantInbox page
// therefore uses `merchantFetch` directly to keep auth tokens from
// crossing wires.

// ============================================================================
// Phase 19 — Surprise Card (customer-side hooks only).
// Tenant-side surprise endpoints use the merchant API token, which lives
// in a disjoint storage key (lib/merchantAuth.ts → orbit_live_*); the
// merchant pool page calls them via `merchantFetch` directly. Same
// reasoning we documented above for the tenant-side inbox endpoints.
// ============================================================================
import {
  SurpriseCardListResponseSchema,
  SurpriseCardResponseSchema,
  type SurpriseCardListResponse,
  type SurpriseCardResponse,
} from "@orbit/api-zod";

const surprisesQueryKey = ["customer", "me", "surprises"] as const;

export function useTodaysSurprises(
  options?: Omit<
    UseQueryOptions<SurpriseCardListResponse, OrbitApiError>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery<SurpriseCardListResponse, OrbitApiError>({
    queryKey: surprisesQueryKey,
    queryFn: () =>
      request({
        method: "GET",
        path: "/api/v1/customer/me/surprises",
        responseSchema: SurpriseCardListResponseSchema,
        authRequired: true,
      }),
    ...options,
  });
}

function invalidateSurprises(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: surprisesQueryKey });
  // Claim → may have created an inbox row + (for VOUCHER_REWARD) a card-side
  // voucher list change. Invalidate inbox + cards so badges/lists refresh.
  void qc.invalidateQueries({ queryKey: ["customer", "me", "inbox"] });
  void qc.invalidateQueries({ queryKey: ["customer", "me", "cards"] });
}

export function useClaimSurprise() {
  const qc = useQueryClient();
  return useMutation<SurpriseCardResponse, OrbitApiError, { tenantId: string }>({
    mutationFn: ({ tenantId }) =>
      request({
        method: "POST",
        path: `/api/v1/customer/me/surprises/${encodeURIComponent(tenantId)}/claim`,
        responseSchema: SurpriseCardResponseSchema,
        authRequired: true,
      }),
    onSuccess: () => invalidateSurprises(qc),
  });
}

export function useSkipSurprise() {
  const qc = useQueryClient();
  return useMutation<SurpriseCardResponse, OrbitApiError, { tenantId: string }>({
    mutationFn: ({ tenantId }) =>
      request({
        method: "POST",
        path: `/api/v1/customer/me/surprises/${encodeURIComponent(tenantId)}/skip`,
        responseSchema: SurpriseCardResponseSchema,
        authRequired: true,
      }),
    onSuccess: () => invalidateSurprises(qc),
  });
}

// ────────────────────────────────────────────────────────────────────
// Phase 20 — Streaks & Milestones
// ────────────────────────────────────────────────────────────────────
import {
  StreakAnalyticsResponseSchema,
  StreakConfigResponseSchema,
  StreakLeaderboardResponseSchema,
  StreakMilestoneListResponseSchema,
  StreakStateListResponseSchema,
  type CustomerMilestone,
  type CustomerStreak,
  type MilestoneKind,
  type StreakAnalyticsResponse,
  type StreakCadence,
  type StreakConfigPatchBody,
  type StreakConfigResponse,
  type StreakLeaderboardEntry,
  type StreakLeaderboardResponse,
  type StreakMilestoneListResponse,
  type StreakStateListResponse,
} from "@orbit/api-zod";

export type {
  CustomerMilestone,
  CustomerStreak,
  MilestoneKind,
  StreakAnalyticsResponse,
  StreakCadence,
  StreakConfigPatchBody,
  StreakConfigResponse,
  StreakLeaderboardEntry,
  StreakLeaderboardResponse,
  StreakMilestoneListResponse,
  StreakStateListResponse,
};

const customerStreaksQueryKey = ["customer", "me", "streaks"] as const;
const customerMilestonesQueryKey = ["customer", "me", "milestones"] as const;
const tenantStreakConfigQueryKey = ["tenant", "streaks", "config"] as const;
const tenantStreakLeaderboardQueryKey = [
  "tenant",
  "streaks",
  "leaderboard",
] as const;
const tenantStreakAnalyticsQueryKey = [
  "tenant",
  "streaks",
  "analytics",
] as const;

export function useCustomerStreaks(
  options?: Omit<
    UseQueryOptions<StreakStateListResponse, OrbitApiError>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery<StreakStateListResponse, OrbitApiError>({
    queryKey: customerStreaksQueryKey,
    queryFn: () =>
      request({
        method: "GET",
        path: "/api/v1/customer/me/streaks",
        responseSchema: StreakStateListResponseSchema,
        authRequired: true,
      }),
    ...options,
  });
}

export function useCustomerMilestones(
  params: { cursor?: string | null; limit?: number } = {},
  options?: Omit<
    UseQueryOptions<StreakMilestoneListResponse, OrbitApiError>,
    "queryKey" | "queryFn"
  >,
) {
  const { cursor = null, limit } = params;
  const qs = new URLSearchParams();
  if (cursor) qs.set("cursor", cursor);
  if (typeof limit === "number") qs.set("limit", String(limit));
  const path = `/api/v1/customer/me/milestones${qs.toString() ? `?${qs.toString()}` : ""}`;
  return useQuery<StreakMilestoneListResponse, OrbitApiError>({
    queryKey: [...customerMilestonesQueryKey, cursor, limit ?? null],
    queryFn: () =>
      request({
        method: "GET",
        path,
        responseSchema: StreakMilestoneListResponseSchema,
        authRequired: true,
      }),
    ...options,
  });
}

export function useTenantStreakConfig(
  options?: Omit<
    UseQueryOptions<StreakConfigResponse, OrbitApiError>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery<StreakConfigResponse, OrbitApiError>({
    queryKey: tenantStreakConfigQueryKey,
    queryFn: () =>
      request({
        method: "GET",
        path: "/api/v1/tenant/streaks/config",
        responseSchema: StreakConfigResponseSchema,
        authRequired: true,
      }),
    ...options,
  });
}

export function useUpdateTenantStreakConfig() {
  const qc = useQueryClient();
  return useMutation<
    StreakConfigResponse,
    OrbitApiError,
    StreakConfigPatchBody
  >({
    mutationFn: (body) =>
      request({
        method: "PATCH",
        path: "/api/v1/tenant/streaks/config",
        body,
        responseSchema: StreakConfigResponseSchema,
        authRequired: true,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tenantStreakConfigQueryKey });
      // A cadence change resets all customer streak rows (D77), so the
      // leaderboard could swing dramatically. Refresh both.
      void qc.invalidateQueries({ queryKey: tenantStreakLeaderboardQueryKey });
    },
  });
}

export function useTenantStreakLeaderboard(
  params: { limit?: number } = {},
  options?: Omit<
    UseQueryOptions<StreakLeaderboardResponse, OrbitApiError>,
    "queryKey" | "queryFn"
  >,
) {
  const { limit } = params;
  const qs = new URLSearchParams();
  if (typeof limit === "number") qs.set("limit", String(limit));
  const path = `/api/v1/tenant/streaks/leaderboard${qs.toString() ? `?${qs.toString()}` : ""}`;
  return useQuery<StreakLeaderboardResponse, OrbitApiError>({
    queryKey: [...tenantStreakLeaderboardQueryKey, limit ?? null],
    queryFn: () =>
      request({
        method: "GET",
        path,
        responseSchema: StreakLeaderboardResponseSchema,
        authRequired: true,
      }),
    ...options,
  });
}

export function useTenantStreakAnalytics(
  params: { from?: string; to?: string } = {},
  options?: Omit<
    UseQueryOptions<StreakAnalyticsResponse, OrbitApiError>,
    "queryKey" | "queryFn"
  >,
) {
  const { from, to } = params;
  const qs = new URLSearchParams();
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);
  const path = `/api/v1/tenant/streaks/analytics${qs.toString() ? `?${qs.toString()}` : ""}`;
  return useQuery<StreakAnalyticsResponse, OrbitApiError>({
    queryKey: [...tenantStreakAnalyticsQueryKey, from ?? null, to ?? null],
    queryFn: () =>
      request({
        method: "GET",
        path,
        responseSchema: StreakAnalyticsResponseSchema,
        authRequired: true,
      }),
    ...options,
  });
}

// ────────────────────────────────────────────────────────────────────
// Phase 21 — Monthly Recap + Year-in-Review (customer-side hooks)
// ────────────────────────────────────────────────────────────────────

import {
  CustomerRecapDetailResponseSchema,
  CustomerRecapListResponseSchema,
  type CustomerRecapDetailResponse,
  type CustomerRecapListResponse,
} from "@orbit/api-zod";

const customerRecapsQueryKey = ["customer", "me", "recaps"] as const;

export function useCustomerRecaps(
  params: { cursor?: string | null; limit?: number } = {},
  options?: Omit<
    UseQueryOptions<CustomerRecapListResponse, OrbitApiError>,
    "queryKey" | "queryFn"
  >,
) {
  const { cursor = null, limit } = params;
  const qs = new URLSearchParams();
  if (cursor) qs.set("cursor", cursor);
  if (typeof limit === "number") qs.set("limit", String(limit));
  const path = `/api/v1/customer/me/recaps${qs.toString() ? `?${qs.toString()}` : ""}`;
  return useQuery<CustomerRecapListResponse, OrbitApiError>({
    queryKey: [...customerRecapsQueryKey, cursor, limit ?? null],
    queryFn: () =>
      request({
        method: "GET",
        path,
        responseSchema: CustomerRecapListResponseSchema,
        authRequired: true,
      }),
    ...options,
  });
}

export function useCustomerRecap(
  recapId: string | null,
  options?: Omit<
    UseQueryOptions<CustomerRecapDetailResponse, OrbitApiError>,
    "queryKey" | "queryFn" | "enabled"
  >,
) {
  return useQuery<CustomerRecapDetailResponse, OrbitApiError>({
    queryKey: [...customerRecapsQueryKey, "detail", recapId],
    queryFn: () =>
      request({
        method: "GET",
        path: `/api/v1/customer/me/recaps/${encodeURIComponent(recapId ?? "")}`,
        responseSchema: CustomerRecapDetailResponseSchema,
        authRequired: true,
      }),
    enabled: typeof recapId === "string" && recapId.length > 0,
    ...options,
  });
}

// =====================================================================
// Phase 26 — Customer plate registration
// =====================================================================
//
// Customer-managed vehicle plates (max 5). All four endpoints live
// under /customer/me/vehicles and are gated by customerAuth on the
// server. Pattern matches Phase 12: a single useQuery for the list,
// three mutations for add/update/delete. Consumers are responsible
// for invalidating ["customer", "me", "vehicles"] on mutation
// success — the hooks themselves stay framework-agnostic so they
// don't import a QueryClient instance (which would break SSR).

import {
  AddVehicleRequestSchema,
  AddVehicleResponseSchema,
  CustomerVehicleListResponseSchema,
  DeleteVehicleResponseSchema,
  UpdateVehicleRequestSchema,
  UpdateVehicleResponseSchema,
  type AddVehicleRequest,
  type AddVehicleResponse,
  type CustomerVehicleListResponse,
  type DeleteVehicleResponse,
  type UpdateVehicleRequest,
  type UpdateVehicleResponse,
} from "@orbit/api-zod";

export const customerVehiclesQueryKey = ["customer", "me", "vehicles"] as const;

export function useGetCustomerVehicles(
  options?: Omit<
    UseQueryOptions<CustomerVehicleListResponse, OrbitApiError>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery<CustomerVehicleListResponse, OrbitApiError>({
    queryKey: [...customerVehiclesQueryKey],
    queryFn: () =>
      request({
        method: "GET",
        path: "/api/v1/customer/me/vehicles",
        responseSchema: CustomerVehicleListResponseSchema,
        authRequired: true,
      }),
    retry: false,
    ...options,
  });
}

export function useAddCustomerVehicle() {
  return useMutation<AddVehicleResponse, OrbitApiError, AddVehicleRequest>({
    mutationFn: (body) =>
      request({
        method: "POST",
        path: "/api/v1/customer/me/vehicles",
        body: AddVehicleRequestSchema.parse(body),
        responseSchema: AddVehicleResponseSchema,
        authRequired: true,
      }),
  });
}

export function useUpdateCustomerVehicle() {
  return useMutation<
    UpdateVehicleResponse,
    OrbitApiError,
    { vehicleId: string; body: UpdateVehicleRequest }
  >({
    mutationFn: ({ vehicleId, body }) =>
      request({
        method: "PATCH",
        path: `/api/v1/customer/me/vehicles/${encodeURIComponent(vehicleId)}`,
        body: UpdateVehicleRequestSchema.parse(body),
        responseSchema: UpdateVehicleResponseSchema,
        authRequired: true,
      }),
  });
}

export function useDeleteCustomerVehicle() {
  return useMutation<DeleteVehicleResponse, OrbitApiError, { vehicleId: string }>({
    mutationFn: ({ vehicleId }) =>
      request({
        method: "DELETE",
        path: `/api/v1/customer/me/vehicles/${encodeURIComponent(vehicleId)}`,
        responseSchema: DeleteVehicleResponseSchema,
        authRequired: true,
      }),
  });
}

// ----- Phase 31 (D103) — password reset (public 3-step) + set/change -----
//
// Step 1 (public): /auth/password-reset/request returns a uniform
// envelope even when the phone is unknown (privacy stance from D103).
// Step 2 (public): /auth/password-reset/verify-otp burns the OTP and
// flips the attempt to OTP_VERIFIED.
// Step 3 (public): /auth/password-reset/complete swaps the customer's
// password_hash and returns a fresh session — same shape as login so
// the PWA can signIn() right after.
// Authed: /customer/me/password sets a password for a passwordless
// customer (Bea-equivalent created via signup-via-join) or rotates an
// existing one. The server requires currentPassword only when the
// account already has a password_hash; the UI reads
// CustomerMeResponse.passwordSet to decide whether to render the
// current-password input.
export function usePasswordResetRequest() {
  return useMutation<
    PasswordResetRequestResponse,
    OrbitApiError,
    PasswordResetRequest
  >({
    mutationFn: (body) =>
      request({
        method: "POST",
        path: "/api/v1/auth/password-reset/request",
        body: PasswordResetRequestSchema.parse(body),
        responseSchema: PasswordResetRequestResponseSchema,
      }),
  });
}

export function usePasswordResetVerifyOtp() {
  return useMutation<
    PasswordResetVerifyOtpResponse,
    OrbitApiError,
    PasswordResetVerifyOtpRequest
  >({
    mutationFn: (body) =>
      request({
        method: "POST",
        path: "/api/v1/auth/password-reset/verify-otp",
        body: PasswordResetVerifyOtpRequestSchema.parse(body),
        responseSchema: PasswordResetVerifyOtpResponseSchema,
      }),
  });
}

export function usePasswordResetComplete() {
  return useMutation<
    PasswordResetCompleteResponse,
    OrbitApiError,
    PasswordResetCompleteRequest
  >({
    mutationFn: (body) =>
      request({
        method: "POST",
        path: "/api/v1/auth/password-reset/complete",
        body: PasswordResetCompleteRequestSchema.parse(body),
        responseSchema: PasswordResetCompleteResponseSchema,
      }),
  });
}

export function useSetCustomerPassword() {
  return useMutation<
    SetCustomerPasswordResponse,
    OrbitApiError,
    SetCustomerPasswordRequest
  >({
    mutationFn: (body) =>
      request({
        method: "POST",
        path: "/api/v1/customer/me/password",
        body: SetCustomerPasswordRequestSchema.parse(body),
        responseSchema: SetCustomerPasswordResponseSchema,
        authRequired: true,
      }),
  });
}

import type {
  PasswordResetCompleteRequest,
  PasswordResetCompleteResponse,
  PasswordResetRequest,
  PasswordResetRequestResponse,
  PasswordResetVerifyOtpRequest,
  PasswordResetVerifyOtpResponse,
  SetCustomerPasswordRequest,
  SetCustomerPasswordResponse,
} from "@orbit/api-zod";
